import React from "react";
import { useParams } from "react-router-dom";
import InputBox from "../Components/InputBox";
import SelectInput from "../Components/SelectInput";
import {
  getAllDocs,
  setDocData,
  storage,
} from "../../../utils/firebase/Firebase.utils";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { useEffect } from "react";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import Modal from "../Components/Modal";
import NoPhoto from "../../assets/blankphoto.webp";
import { useCompany } from "../../../context/CompanyContext";
import CompanyTerms from "../../../components/CompanyTerms";
import {
  MdModeEdit,
  MdSave,
  MdCancel,
  MdCloudUpload,
  MdWarning,
} from "react-icons/md";
import {
  FaUser,
  FaBuilding,
  FaHome,
  FaPhone,
  FaEnvelope,
  FaCalendarAlt,
  FaUsers,
  FaFileContract,
} from "react-icons/fa";
import BasicInformationForm from "./BasicInformationForm";

export default function BasicInformation({ tenant }) {
  const { id } = useParams();
  const [formData, setFormData] = React.useState(tenant);
  const [disabled, setDisabled] = React.useState(true);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [residents, setResidents] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [editMode, setEditMode] = React.useState(false);
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [showConfirmCancel, setShowConfirmCancel] = React.useState(false);

  const currentUser = useSelector((state) => state.userObject);
  const { terms, getCapitalizedTerm } = useCompany();

  useEffect(() => {
    const fetchResidents = async () => {
      const residents = await getAllDocs("residents");

      const unitNumbers = residents.map((resident) => {
        return {
          identifier: resident.identifier,
          value: resident.identifier,
          label: resident.owner1 + " - " + resident.identifier,
        };
      });

      unitNumbers.sort((a, b) => {
        if (a.identifier < b.identifier) {
          return -1;
        }
        if (a.identifier > b.identifier) {
          return 1;
        }
        return 0;
      });

      setResidents(unitNumbers);
      setIsLoading(false);
    };

    fetchResidents();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearchChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({ ...prev, lot: value }));
    setSearchTerm(value);
  };

  const handleResidentSelect = (e, resident) => {
    setFormData((prev) => ({ ...prev, lot: resident.value }));
    setSearchTerm("");
  };

  const filteredResidents = residents.filter(
    (resident) =>
      resident.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resident.identifier?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const leaseTypeOptions = [
    { label: "Personal Lease", value: "Personal Lease" },
    { label: "Corporate Lease", value: "Corporate Lease" },
  ];

  const handleSelectLeaseType = (e) => {
    setFormData((prev) => ({ ...prev, leaseType: e.value }));
  };

  const handleEditMode = () => {
    if (editMode) {
      // If in edit mode, show confirmation before canceling
      setShowConfirmCancel(true);
    } else {
      // Enter edit mode
      setEditMode(true);
      setDisabled(false);
    }
  };

  const handleConfirmCancel = (confirm) => {
    setShowConfirmCancel(false);
    if (confirm) {
      setFormData(tenant);
      setDisabled(true);
      setEditMode(false);
      toast.success("Changes have been cancelled");
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (window.confirm("Are you sure you want to save changes?")) {
      setIsProcessing(true);
      const docObject = {
        ...formData,
        updatedAt: new Date().toLocaleString(),
        updatedBy: currentUser.email,
      };

      try {
        const res = await setDocData("tenants", id, docObject);
        if (res) {
          toast.success("Changes have been saved");
        }
      } catch (error) {
        toast.error("Error saving changes");
        console.error(error);
      } finally {
        setIsProcessing(false);
        setDisabled(true);
        setEditMode(false);
      }
    }
  };

  const allowableAccess = ["admin", "manager", "maker", "super-admin"];
  const userRoles = currentUser.roles;

  const handleUpload = (e) => {
    e.preventDefault();

    const file = e.target.files[0];

    if (!file) {
      toast.error("No file selected");
      return;
    }

    const storageRef = ref(storage, `tenants/${id}/primaryPhoto`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setProgress(progress);
      },
      (error) => {},
      () => {
        getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
          setFormData((prev) => ({ ...prev, primaryPhoto: downloadURL }));
          setDocData("tenants", id, { primaryPhoto: downloadURL });
          toast.success("Photo Uploaded Successfully");
          setProgress(0);
        });
      }
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#363062]"></div>
      </div>
    );
  }

  const InfoCard = ({ title, icon: Icon, children }) => (
    <div className="bg-white rounded-lg border border-gray-100 p-4 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-[#363062]" />
        <h3 className="text-sm font-medium text-[#363062]">{title}</h3>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );

  const InfoItem = ({ label, value, editValue, name, type = "text" }) => (
    <div className="flex items-start justify-between group">
      <span className="text-xs text-gray-500">{label}</span>
      {editMode ? (
        <InputBox
          value={editValue}
          onChange={handleChange}
          name={name}
          type={type}
          className="w-48 text-right bg-[#363062]/5 border-[#363062]/20 focus:border-[#363062] focus:ring-1 focus:ring-[#363062] transition-colors duration-200 px-3 py-1.5"
        />
      ) : (
        <span className="text-sm font-medium text-gray-900">{value}</span>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-[#363062]">
            {formData.firstName} {formData.lastName}
          </h2>
          <p className="text-sm text-gray-500">Tenant Information</p>
        </div>
        {allowableAccess.some((role) => userRoles.includes(role)) && (
          <div className="flex items-center gap-2">
            {editMode ? (
              <>
                <button
                  type="button"
                  onClick={handleEditMode}
                  disabled={isProcessing}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <MdCancel className="w-4 h-4 mr-1.5" />
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isProcessing}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-[#363062] rounded-md hover:bg-[#363062]/90 transition-colors duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isProcessing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1.5"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <MdSave className="w-4 h-4 mr-1.5" />
                      Save Changes
                    </>
                  )}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={handleEditMode}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-[#363062] bg-[#363062]/10 rounded-md hover:bg-[#363062]/20 transition-colors duration-200"
              >
                <MdModeEdit className="w-4 h-4 mr-1.5" />
                Edit Information
              </button>
            )}
          </div>
        )}
      </div>

      {/* Form Component */}
      <BasicInformationForm
        tenant={tenant}
        formData={formData}
        editMode={editMode}
        handleChange={handleChange}
        handleUpload={handleUpload}
        handleSearchChange={handleSearchChange}
        handleResidentSelect={handleResidentSelect}
        handleSelectLeaseType={handleSelectLeaseType}
        searchTerm={searchTerm}
        filteredResidents={filteredResidents}
        leaseTypeOptions={leaseTypeOptions}
        getCapitalizedTerm={getCapitalizedTerm}
        progress={progress}
      />

      {/* Cancel Confirmation Modal */}
      {showConfirmCancel && (
        <Modal>
          <div className="bg-white rounded-lg shadow-lg p-4 text-center max-w-xs mx-auto border border-gray-200">
            <div className="flex justify-center mb-2">
              <div className="rounded-full bg-red-100 p-2">
                <MdWarning className="w-6 h-6 text-red-500" />
              </div>
            </div>
            <h3 className="text-sm font-medium text-gray-900 mb-2">
              Discard Changes?
            </h3>
            <p className="text-xs text-gray-500 mb-4">
              Your changes will not be saved.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => handleConfirmCancel(false)}
                className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Continue Editing
              </button>
              <button
                type="button"
                onClick={() => handleConfirmCancel(true)}
                className="px-3 py-1.5 text-xs font-medium text-white bg-red-500 rounded-md hover:bg-red-600"
              >
                Discard Changes
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
